import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { lookup } from '@stitch/pbx-adapter/describe';
import { api, type CallRecord, type MentionView, type VoicemailView } from '../../api/client';
import { PhonePageHead } from '../../components/PhoneApp';
import { useTenant } from '../../components/Shell';
import { Empty, LoadError, Loading, Notice } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { clockTime, dayHeading, dayOf, shownNumber } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { mentionsMe, useMessagesLive } from '../../lib/messages';
import { plainPreview } from '../../lib/richtext';
import { voicemailListed } from '../../lib/voicemail';
import { useData } from '../../lib/useData';
import { useRememberList } from './MessagesPhone';

/**
 * The phone app's Activity tab: what's come your way, newest first. Messages that mention you,
 * and (with a phone) missed calls and voicemail. Tap one to go to it.
 */

type Item =
  | { kind: 'mention'; at: string; key: string; m: MentionView }
  | { kind: 'call'; at: string; key: string; c: CallRecord; who: string }
  | {
      kind: 'voicemail';
      at: string;
      key: string;
      v: VoicemailView;
      who: string;
    };
type Show = 'all' | 'mention' | 'call' | 'voicemail';

const SHOW: { id: Show; label: string; phone?: boolean }[] = [
  { id: 'all', label: 'All' },
  { id: 'mention', label: 'Mentions' },
  { id: 'call', label: 'Missed calls', phone: true },
  { id: 'voicemail', label: 'Voicemail', phone: true }
];

export function PhoneActivity() {
  const { tenant } = useTenant();
  const d = tenant.domain;
  const { state } = useAuth();
  const member = state?.memberships?.find((x) => x.tenant === d.toLowerCase());
  if (!member || !state?.user) {
    return (
      <div className="content">
        <Notice icon="lock" title="Activity is for the company’s own people">
          Mentions, missed calls and voicemail belong to the people in {tenant.name}.
        </Notice>
      </div>
    );
  }
  return <Feed domain={d} myId={state.user.id} extension={member.extension} />;
}

function Feed({ domain, myId, extension }: { domain: string; myId: string; extension: string | null }) {
  useRememberList();
  const base = `/t/${encodeURIComponent(domain)}`;
  const [show, setShow] = useState<Show>('all');
  const mentions = useData(() => api.mentions(domain), [domain]);
  const calls = useData(() => (extension ? api.myCalls(domain).catch(() => null) : Promise.resolve(null)), [domain, extension], { topics: ['calls'] });
  const box = useData(async () => {
    if (!extension) return null;
    const readAt = Date.now();
    const got = await api.voicemail(domain).catch(() => null);
    // A notice for a message heard or deleted on a desk phone goes, and the count follows.
    if (got) voicemailListed(domain, got, readAt);
    return got;
  }, [domain, extension]);
  const dir = useData(() => (extension ? api.directory(domain).catch(() => undefined) : Promise.resolve(undefined)), [domain, extension]);

  // A new mention, or reading one elsewhere: the list follows.
  const timer = useRef<number | undefined>(undefined);
  const { reload } = mentions;
  useEffect(() => () => window.clearTimeout(timer.current), []);
  useMessagesLive((e) => {
    if (e.tenant !== domain.toLowerCase()) return;
    if ((e.type === 'message' && e.message && mentionsMe(e.message, myId)) || e.type === 'read') {
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(reload, 500);
    }
  });

  const name = (p: CallRecord['from']) => p.name || (p.number ? lookup(dir.data, p.number).name : '') || (p.number ? shownNumber(p.number) : 'Unknown');
  const items: Item[] = [
    ...(mentions.data?.mentions ?? []).map((m): Item => ({
      kind: 'mention',
      at: m.at,
      key: `m:${m.id}`,
      m
    })),
    ...(calls.data?.calls ?? [])
      .filter((c) => c.missed && c.from.number !== extension)
      .map((c): Item => ({
        kind: 'call',
        at: c.start,
        key: `c:${c.id}`,
        c,
        who: name(c.from)
      })),
    ...(box.data?.messages ?? []).map((v): Item => ({
      kind: 'voicemail',
      at: v.time,
      key: `v:${v.id}`,
      v,
      who: name(v.from)
    }))
  ]
    .filter((x) => show === 'all' || x.kind === show)
    .sort((a, b) => b.at.localeCompare(a.at));

  const days: { day: string; heading: string; items: Item[] }[] = [];
  for (const x of items) {
    const day = dayOf(x.at);
    const last = days[days.length - 1];
    if (last?.day === day) last.items.push(x);
    else days.push({ day, heading: dayHeading(x.at), items: [x] });
  }
  const loading = mentions.loading && !mentions.data;

  return (
    <div className="phome pfeed">
      <PhonePageHead title="Activity" />
      {extension && (
        <div className="pfeed-show" role="group" aria-label="Show">
          {SHOW.filter((x) => !x.phone || extension).map((x) => (
            <button key={x.id} type="button" className="pfeed-chip" aria-pressed={show === x.id} onClick={() => setShow(x.id)}>
              {x.label}
            </button>
          ))}
        </div>
      )}
      {mentions.error && !mentions.data ? (
        <div className="card">
          <LoadError error={mentions.error} retry={mentions.reload} />
        </div>
      ) : loading ? (
        <Loading label="Loading activity" />
      ) : days.length === 0 ? (
        <Empty title={show === 'all' ? 'Nothing yet' : 'None of these yet'}>
          {extension ? 'When someone mentions you, or you miss a call or get voicemail, it shows here.' : 'When someone mentions you (@your name), it shows here.'}
        </Empty>
      ) : (
        days.map((g) => (
          <section key={g.day} className="pfeed-day" aria-label={g.heading}>
            <h2 className="pfeed-heading">{g.heading}</h2>
            <ul className="pfeed-list">
              {g.items.map((x) => (
                <li key={x.key}>
                  <FeedItem x={x} base={base} />
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}

function FeedItem({ x, base }: { x: Item; base: string }) {
  if (x.kind === 'mention') {
    const m = x.m;
    const where = m.conversation.kind === 'channel' ? `#${m.conversation.title}` : m.conversation.title;
    const to = `${base}/messages/${m.conversation.id}?m=${encodeURIComponent(m.id)}${m.threadRootId && !m.alsoInChannel ? `&thread=${encodeURIComponent(m.threadRootId)}` : ''}`;
    return (
      <Link to={to} className={`pfeed-item${m.read ? '' : ' new'}`}>
        <span className="pfeed-icon mention" aria-hidden="true">
          <Icon name="at" size={18} strokeWidth={2.1} />
        </span>
        <span className="pfeed-main">
          <span className="pfeed-top">
            <span className="pfeed-what">
              <strong>{m.author}</strong> mentioned you {m.threadRootId ? 'in a thread in ' : 'in '}
              <strong>{where}</strong>
            </span>
            <span className="pfeed-when">{clockTime(m.at)}</span>
          </span>
          <span className="pfeed-body" data-private="">
            {plainPreview(m.body)}
          </span>
        </span>
        {!m.read && <span className="sr-only">, not read yet</span>}
      </Link>
    );
  }
  if (x.kind === 'call') {
    return (
      <Link to={`${base}/me/calls`} className="pfeed-item">
        <span className="pfeed-icon call" aria-hidden="true">
          <Icon name="phone" size={18} />
        </span>
        <span className="pfeed-main">
          <span className="pfeed-top">
            <span className="pfeed-what">
              Missed call from <strong>{x.who}</strong>
            </span>
            <span className="pfeed-when">{clockTime(x.at)}</span>
          </span>
          {x.c.from.number && shownNumber(x.c.from.number) !== x.who && <span className="pfeed-body muted">{shownNumber(x.c.from.number)}</span>}
        </span>
      </Link>
    );
  }
  const v = x.v;
  const length = `${Math.floor(v.seconds / 60)}:${String(v.seconds % 60).padStart(2, '0')}`;
  return (
    <Link to={`${base}/me/calls?show=voicemail`} className={`pfeed-item${v.isNew ? ' new' : ''}`}>
      <span className="pfeed-icon voicemail" aria-hidden="true">
        <Icon name="mail" size={18} />
      </span>
      <span className="pfeed-main">
        <span className="pfeed-top">
          <span className="pfeed-what">
            Voicemail from <strong>{x.who}</strong> · {length}
          </span>
          <span className="pfeed-when">{clockTime(x.at)}</span>
        </span>
        {v.transcript?.status === 'done' && v.transcript.text ? (
          <span className="pfeed-body" data-private="">
            {v.transcript.text}
          </span>
        ) : v.transcript?.status === 'pending' ? (
          <span className="pfeed-body muted">Writing it out…</span>
        ) : null}
      </span>
      {v.isNew && <span className="sr-only">, new</span>}
    </Link>
  );
}
